/**
 * RealityAI auth background — "planet field".
 *
 * Decorative Canvas scene: a large rotating sphere with a dense node grid,
 * thin ordered mesh lines, Natural Earth land (local GeoJSON, d3-geo
 * orthographic projection), soft cyan light pulses, gentle mouse parallax
 * and small dim drifting outer particles (no tails, no orbit rings).
 *
 * Reference: demo.html approved by the owner (realityai-planet-final).
 * Desktop density/speed/brightness match that reference exactly; mobile
 * (<721px wide) runs a reduced profile (lower DPR cap, fewer stars, no
 * shadowBlur, fewer pulses) for ~30 FPS.
 *
 * The module owns its full lifecycle: RAF loop, ResizeObserver, pointer,
 * visibility and reduced-motion listeners. Call dispose() on unmount —
 * it cancels the RAF and removes every listener (StrictMode-safe).
 *
 * No network access, no external assets, no WebGL. Colors: #060B12,
 * #22D3EE, #38BDF8 family. No violet.
 */
import { geoOrthographic, geoPath } from "d3-geo";
import type { GeoPermissibleObjects } from "d3-geo";
import land from "./land-data.json";

export interface PlanetFieldScene {
  isPaused: () => boolean;
  setPaused: (value: boolean) => void;
  dispose: () => void;
}

export interface PlanetFieldOptions {
  /** Called when the effective pause state changes (user toggle or reduced motion). */
  onPauseChange?: (paused: boolean) => void;
}

interface GridNode {
  x: number;
  y: number;
  z: number;
}

interface ProjectedPoint {
  x: number;
  y: number;
  z: number;
}

interface DriftParticle {
  x: number;
  y: number;
  s: number;
  v: number;
}

/** Sphere grid: rows x cols nodes on the unit sphere (same as reference). */
const ROWS = 23;
const COLS = 48;

function buildGrid(): { nodes: GridNode[]; edges: [number, number][] } {
  const nodes: GridNode[] = [];
  const edges: [number, number][] = [];
  for (let j = 0; j <= ROWS; j++) {
    const lat = -Math.PI / 2 + (j * Math.PI) / ROWS;
    for (let i = 0; i < COLS; i++) {
      const a = (i * Math.PI * 2) / COLS;
      nodes.push({
        x: Math.cos(lat) * Math.cos(a),
        y: Math.sin(lat),
        z: Math.cos(lat) * Math.sin(a),
      });
      if (j > 0) edges.push([j * COLS + i, (j - 1) * COLS + i]);
      if (j > 0 && j < ROWS) edges.push([j * COLS + i, j * COLS + ((i + 1) % COLS)]);
    }
  }
  return { nodes, edges };
}

function buildStars(count: number): DriftParticle[] {
  return Array.from({ length: count }, () => ({
    x: Math.random(),
    y: Math.random(),
    s: 0.5 + Math.random() * 1.5,
    v: Math.random(),
  }));
}

const GRID = buildGrid();
const LAND = land as GeoPermissibleObjects;

/**
 * Mount the planet field into `root` (a positioned container that provides
 * the scene size) using `canvas`. Returns the scene handle.
 */
export function mountPlanetField(
  root: HTMLElement,
  canvas: HTMLCanvasElement,
  { onPauseChange = () => {} }: PlanetFieldOptions = {},
): PlanetFieldScene {
  const maybeCtx = canvas.getContext("2d");
  if (!maybeCtx) {
    // Static CSS fallback is painted by the page (auth-planet class).
    onPauseChange(true);
    return { isPaused: () => true, setPaused: () => {}, dispose: () => {} };
  }
  const ctx: CanvasRenderingContext2D = maybeCtx;

  const reduced = matchMedia("(prefers-reduced-motion: reduce)");

  let w = 0;
  let h = 0;
  let time = 0;
  let last = 0;
  let raf = 0;
  let mx = 0;
  let my = 0;
  let px = 0;
  let py = 0;
  let userPaused = false;
  let disposed = false;
  let mobile = w < 721;
  let stars = buildStars(mobile ? 90 : 180);

  function draw(): void {
    if (!ctx || !w || !h) return;
    ctx.clearRect(0, 0, w, h);

    const cx = w * 0.5;
    const cy = h * 0.49;
    const r = Math.min(h * 0.455, w * 0.475);
    px += (mx - px) * 0.035;
    py += (my - py) * 0.035;

    const still = reduced.matches;
    const t = still ? 0 : time;

    // Ambient glow behind the sphere (reference colors).
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.8);
    glow.addColorStop(0, "#0b586d38");
    glow.addColorStop(0.65, "#0a344522");
    glow.addColorStop(1, "#060b1200");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    // Outer drifting particles: small, dim, common flow, NO tails.
    if (!still) {
      for (const s of stars) {
        const z = 0.35 + s.v * 0.65;
        const speed = 10 + z * 24;
        const xx = (((s.x * w - t * speed) % (w + 160)) + (w + 160)) % (w + 160) - 80;
        const yy = (((s.y * h + t * speed * 0.19) % (h + 80)) + (h + 80)) % (h + 80) - 40;
        ctx.fillStyle = `rgba(130,205,225,${0.1 + z * 0.16})`;
        ctx.beginPath();
        ctx.arc(xx, yy, s.s * 0.38, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Sphere body (slightly translucent so the glow reads through).
    const ox = still ? 0 : px * 12;
    const oy = still ? 0 : py * 9;
    ctx.fillStyle = "rgba(6,16,25,0.78)";
    ctx.beginPath();
    ctx.arc(cx + ox, cy + oy, r, 0, Math.PI * 2);
    ctx.fill();

    // Rotation: slow (0.045 rad/s in the reference) + gentle parallax.
    const a = -0.85 + t * 0.045 + (still ? 0 : px * 0.1);
    const tilt = 0.2 + (still ? 0 : py * 0.07);

    const points: ProjectedPoint[] = GRID.nodes.map((n) => {
      const x = n.x * Math.cos(a) + n.z * Math.sin(a);
      const z1 = -n.x * Math.sin(a) + n.z * Math.cos(a);
      const y = -n.y * Math.cos(tilt) + z1 * Math.sin(tilt);
      const zz = n.y * Math.sin(tilt) + z1 * Math.cos(tilt);
      return { x: cx + x * r + ox, y: cy + y * r + oy, z: zz };
    });

    // Continents (local Natural Earth data) rotate with the sphere.
    const centerLon = (-a * 180) / Math.PI;
    const centerLat = (tilt * 180) / Math.PI;
    const geo = geoOrthographic()
      .translate([cx + ox, cy + oy])
      .scale(r)
      .rotate([-centerLon, -centerLat, 0])
      .clipAngle(90);
    const gp = geoPath(geo, ctx);
    ctx.fillStyle = "rgba(34,211,238,0.075)";
    ctx.strokeStyle = "rgba(115,232,244,0.65)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    gp(LAND);
    ctx.fill();
    ctx.stroke();

    // Rim glow.
    if (!mobile) {
      ctx.strokeStyle = "rgba(80,230,250,0.52)";
      ctx.lineWidth = 0.9;
      ctx.shadowColor = "#22d3ee";
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(cx + ox, cy + oy, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    } else {
      ctx.strokeStyle = "rgba(80,230,250,0.52)";
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.arc(cx + ox, cy + oy, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Mesh edges with travelling soft pulses.
    ctx.lineWidth = 0.65;
    const shadow = mobile ? 0 : 16;
    const halos = mobile ? 0 : 13;
    for (let k = 0; k < GRID.edges.length; k++) {
      const [i, j] = GRID.edges[k];
      const p = points[i];
      const q = points[j];
      const alpha = 0.025 + ((p.z + q.z + 2) / 4) * 0.33;
      ctx.strokeStyle = `rgba(34,211,238,${alpha})`;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(q.x, q.y);
      ctx.stroke();
      if (still) continue;
      const phase = (t * 0.12 + k * 0.173) % 9;
      if (phase < 0.65 && p.z + q.z > -0.2) {
        const tt = phase / 0.65;
        const ix = p.x + (q.x - p.x) * tt;
        const iy = p.y + (q.y - p.y) * tt;
        ctx.fillStyle = "#8edbe4";
        ctx.shadowColor = "#22d3ee";
        ctx.shadowBlur = shadow;
        ctx.beginPath();
        ctx.arc(ix, iy, 2.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        if (halos) {
          const halo = ctx.createRadialGradient(ix, iy, 0, ix, iy, halos);
          halo.addColorStop(0, "#a5faff55");
          halo.addColorStop(1, "#22d3ee00");
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(ix, iy, halos, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Surface nodes (front-to-back so front ones render on top).
    const sorted = [...points].sort((a1, b1) => a1.z - b1.z);
    for (let i = 0; i < sorted.length; i++) {
      const p = sorted[i];
      const brightness = 0.22 + (p.z + 1) * 0.33;
      ctx.fillStyle = `rgba(100,235,250,${brightness})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, i % 17 === 0 ? 2 : 0.85, 0, Math.PI * 2);
      if (i % 17 === 0 && !mobile) {
        ctx.shadowColor = "#22d3ee";
        ctx.shadowBlur = 15;
      }
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // Soft cyan light pulses on the visible surface (reference: 9 on
    // desktop, 4 on the mobile profile).
    if (!still) {
      const pulses = mobile ? 4 : 9;
      for (let k = 0; k < pulses; k++) {
        const index = (k * 113 + Math.floor(t * 0.08) * 41) % sorted.length;
        const p = sorted[index];
        const pulse = Math.pow(Math.max(0, Math.sin(t * 0.65 + k * 2.3)), 14);
        if (p.z > -0.1 && pulse > 0.03) {
          const radius = 5 + pulse * 25;
          const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
          g.addColorStop(0, `rgba(220,255,255,${pulse * 0.65})`);
          g.addColorStop(0.15, `rgba(100,240,255,${pulse * 0.48})`);
          g.addColorStop(1, "rgba(34,211,238,0)");
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  function resize(): void {
    w = root.clientWidth;
    h = root.clientHeight;
    const d = mobile ? Math.min(devicePixelRatio || 1, 1.25) : Math.min(devicePixelRatio || 1, 1.6);
    canvas.width = Math.max(1, Math.round(w * d));
    canvas.height = Math.max(1, Math.round(h * d));
    ctx.setTransform(d, 0, 0, d, 0, 0);
    draw();
  }

  function isPaused(): boolean {
    return userPaused || reduced.matches;
  }

  function frame(now: number): void {
    if (disposed) return;
    time += Math.min((now - (last || now)) / 1000, 0.05);
    last = now;
    draw();
    if (!isPaused() && !document.hidden) raf = requestAnimationFrame(frame);
  }

  function start(): void {
    cancelAnimationFrame(raf);
    last = 0;
    if (disposed) return;
    onPauseChange(isPaused());
    if (!isPaused() && !document.hidden) {
      raf = requestAnimationFrame(frame);
    } else {
      draw();
    }
  }

  function move(e: PointerEvent): void {
    if (isPaused() || !w || !h) return;
    const b = root.getBoundingClientRect();
    mx = (e.clientX - b.left) / w - 0.5;
    my = (e.clientY - b.top) / h - 0.5;
  }

  function leave(): void {
    mx = 0;
    my = 0;
  }

  function onMotionChange(): void {
    mx = 0;
    my = 0;
    px = 0;
    py = 0;
    start();
  }

  // A background canvas cannot receive pointer events (pointer-events:none),
  // so listen on the nearest interactive ancestor (the auth layout).
  const pointerTarget = root.parentElement || root;
  pointerTarget.addEventListener("pointermove", move);
  pointerTarget.addEventListener("pointerleave", leave);
  document.addEventListener("visibilitychange", start);
  reduced.addEventListener("change", onMotionChange);

  function onResize(): void {
    if (disposed) return;
    const wasMobile = mobile;
    mobile = w < 721;
    if (mobile !== wasMobile) {
      // Rebuild the reduced profile when crossing the breakpoint.
      stars = buildStars(mobile ? 90 : 180);
    }
    resize();
  }
  const observer = new ResizeObserver(onResize);
  observer.observe(root);

  // Initial paint + loop start.
  resize();
  start();

  return {
    isPaused,
    setPaused(value: boolean): void {
      userPaused = Boolean(value);
      start();
    },
    dispose(): void {
      disposed = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      pointerTarget.removeEventListener("pointermove", move);
      pointerTarget.removeEventListener("pointerleave", leave);
      document.removeEventListener("visibilitychange", start);
      reduced.removeEventListener("change", onMotionChange);
      ctx.clearRect(0, 0, w, h);
    },
  };
}
